// @flow

import React from "react";
import { connect } from "react-redux";
import { localize } from "redux-i18n";
import { GSGrid, SectionComponent, Pagination, Button, ConfirmationModal } from "@components";
import { compose } from "redux";
import { formatDateTime } from "@transformers/date";
import type {
  AbsenceDocument,
  DeleteAbsenceDocumentModal as DeleteAbsenceDocumentModalType,
} from "@modules/shared/reducer/employee-absences/types";
import actionCreators from "@modules/shared/actionCreator";
import {
  getAbsenceDocuments,
  getAbsenceDocumentsPagination,
  getAddAbsenceDocumentModal,
  getDeleteAbsenceDocumentModal,
} from "@modules/shared/selectors/employee-absences";
import type { State } from "@store/reducers";
import { AddAbsenceDocumentModal } from "@modules/shared/components";
import type { PaginationExtendedType, ModalType } from "@types";

type OwnProps = $Exact<{
  id: string,
  clientId: ?number,
  employeeId: number,
  absenceId: number,
}>;

type PropsFromState = $Exact<{
  documents: Array<AbsenceDocument>,
  pagination: PaginationExtendedType,
  addAbsenceDocumentModal: ModalType,
  deleteAbsenceDocumentModal: DeleteAbsenceDocumentModalType,
}>;

type PropsFromDispatch = $Exact<{
  getDocuments: typeof actionCreators.GetEmployeeAbsenceDocuments,
  toggleDocumentModal: typeof actionCreators.ToggleEmployeeAbsenceDocumentModal,
  createDocument: typeof actionCreators.CreateEmployeeAbsenceDocument,
  deleteDocument: typeof actionCreators.DeleteEmployeeAbsenceDocument,
  toggleDeleteDocumentModal: typeof actionCreators.ToggleDeleteEmployeeAbsenceDocumentModal,
  downloadDocument: typeof actionCreators.GetEmployeeAbsenceDocument,
}>;

type Props = $Exact<{
  ...OwnProps,
  ...PropsFromState,
  ...PropsFromDispatch,
  ...PropsFromLocalize,
}>;

type ComponentState = $Exact<{
  isSectionOpen: boolean,
}>;

export class AbsenceDocuments extends React.Component<Props, ComponentState> {
  state = {
    isSectionOpen: true,
  };

  componentDidMount() {
    this.props.getDocuments({
      clientId: this.props.clientId,
      absenceId: this.props.absenceId,
    });
  }

  showAddDocumentModal = () => {
    this.props.toggleDocumentModal({
      visible: true,
    });
  };

  closeDocumentModal = () => {
    this.props.toggleDocumentModal({
      visible: false,
    });
  };

  handleFormSubmit = (formData: Object) => {
    this.handleCreateDocument(formData);
    this.closeDocumentModal();
  };

  handleCreateDocument = (formData: Object) => {
    this.props.createDocument({
      clientId: this.props.clientId,
      absenceId: this.props.absenceId,
      document: formData,
    });
  };

  handlePaginationChange = (page: number) => {
    this.props.getDocuments({
      clientId: this.props.clientId,
      absenceId: this.props.absenceId,
      page,
    });
  };

  handleViewDocument = (document: AbsenceDocument) => () => {
    const { clientId, absenceId } = this.props;

    this.props.downloadDocument({
      clientId,
      absenceId,
      fileId: document.id,
      fileName: document.name,
    });
  };

  showDeleteDocumentModal = (documentId: number) => {
    this.props.toggleDeleteDocumentModal({
      visible: true,
      documentId,
    });
  };

  closeDeleteDocumentModal = () => {
    this.props.toggleDeleteDocumentModal({
      visible: false,
    });
  };

  handleDeleteDocument = () => {
    this.props.deleteDocument({
      clientId: this.props.clientId,
      absenceId: this.props.absenceId,
      fileId: this.props.deleteAbsenceDocumentModal.documentId,
    });
    this.closeDeleteDocumentModal();
  };

  get columns() {
    return [
      {
        key: "id",
        name: this.props.t("DOCUMENT_ID"),
        width: "min-content",
      },
      {
        key: "documentType",
        name: this.props.t("DOCUMENT_TYPE"),
        renderCell: ({ row }: { row: AbsenceDocument }) => this.props.t(row.documentType),
        width: "min-content",
        align: "left",
      },
      {
        key: "name",
        name: this.props.t("DOCUMENT_NAME"),
        align: "left",
      },
      {
        key: "uploadedBy",
        name: this.props.t("UPLOADED_BY"),
        renderCell: ({ row }: { row: AbsenceDocument }) =>
          row.createdBy ? `${row.createdBy.firstName} ${row.createdBy.lastName}` : "",
        width: "min-content",
        align: "left",
      },
      {
        key: "dateUploaded",
        name: this.props.t("DATE_UPLOADED"),
        renderCell: ({ row }: { row: AbsenceDocument }) => formatDateTime(row.createdAt),
        width: "min-content",
      },
      {
        key: "download",
        name: this.props.t("DOWNLOAD"),
        renderCell: ({ row }: { row: AbsenceDocument }) =>
          !row.isArchived && (
            <span className="material-icons pointer" onClick={this.handleViewDocument(row)}>
              file_download
            </span>
          ),
        width: "min-content",
      },
      {
        name: this.props.t("DELETE"),
        key: "delete",
        hidden: (row: AbsenceDocument) => row.isEditableByLoggedInUser,
        onCellClick: (row: AbsenceDocument) =>
          row.isEditableByLoggedInUser && this.showDeleteDocumentModal(row.id),
        renderCell: ({ row }: { row: AbsenceDocument }) => (
          <Button
            color="secondary"
            disabled={!row.isEditableByLoggedInUser}
            materialIcon="delete"
          />
        ),
        width: "min-content",
      },
    ];
  }

  toggleSection = () => {
    this.setState({
      isSectionOpen: !this.state.isSectionOpen,
    });
  };

  get data() {
    return this.props.documents.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  }

  render() {
    return (
      <SectionComponent
        id={this.props.id}
        title={<h5>{this.props.t("DOCUMENTS")}</h5>}
        wrapperClassName="flex-column"
        rightLink={
          <Button materialIcon="upload_file" onClick={this.showAddDocumentModal}>
            {this.props.t("UPLOAD")}
          </Button>
        }
        collapsable
        t={this.props.t}
        onTitleClick={this.toggleSection}
        isOpen={this.state.isSectionOpen}
      >
        <GSGrid
          rows={this.data}
          columns={this.columns}
          noResultsText={this.props.t("NO_DOCUMENTS_MESSAGE")}
        />
        <Pagination
          className="my-3 d-flex flex-fill justify-content-center"
          currentPage={this.props.pagination.pageNumber}
          totalPages={this.props.pagination.totalPages}
          onChange={this.handlePaginationChange}
        />
        {this.props.addAbsenceDocumentModal.visible && (
          <AddAbsenceDocumentModal
            onDismissHandler={this.closeDocumentModal}
            onSubmit={this.handleFormSubmit}
          />
        )}
        {this.props.deleteAbsenceDocumentModal.visible && (
          <ConfirmationModal
            headerText={this.props.t("DELETE_DOCUMENT")}
            bodyContent={this.props.t("DELETE_DOCUMENT_CONFIRMATION")}
            confirmBtnLabel={this.props.t("DELETE")}
            confirmBtnIcon="delete_forever"
            onCancel={this.closeDeleteDocumentModal}
            onConfirm={this.handleDeleteDocument}
          />
        )}
      </SectionComponent>
    );
  }
}

const mapStateToProps = (state: State): PropsFromState => ({
  documents: getAbsenceDocuments(state),
  pagination: getAbsenceDocumentsPagination(state),
  addAbsenceDocumentModal: getAddAbsenceDocumentModal(state),
  deleteAbsenceDocumentModal: getDeleteAbsenceDocumentModal(state),
});

const mapDispatchToProps: PropsFromDispatch = {
  getDocuments: actionCreators.GetEmployeeAbsenceDocuments,
  toggleDocumentModal: actionCreators.ToggleEmployeeAbsenceDocumentModal,
  createDocument: actionCreators.CreateEmployeeAbsenceDocument,
  deleteDocument: actionCreators.DeleteEmployeeAbsenceDocument,
  toggleDeleteDocumentModal: actionCreators.ToggleDeleteEmployeeAbsenceDocumentModal,
  downloadDocument: actionCreators.GetEmployeeAbsenceDocument,
};

export default compose(localize(), connect(mapStateToProps, mapDispatchToProps))(AbsenceDocuments);
